import type { parameterStruct } from "./dataStruct";

export const CALC_RESULT_VERSION = 1;

// One loading element with its own values.
export interface CalcElement {
  uid: string;
  payLoadCodeType: number;
  designator: string;
  values: Record<string, string>;
}

export interface CalcResult {
  v: number;
  inputs: Record<string, number>;
  upstream?: string;
  values: Record<string, string>;
  elements?: CalcElement[];
}

export function buildCalcResult(parts: Omit<CalcResult, "v">): string {
  const inputs: Record<string, number> = {};
  for (const [key, value] of Object.entries(parts.inputs ?? {})) {
    if (Number.isFinite(value)) inputs[key] = value;
  }
  return JSON.stringify({ ...parts, inputs, v: CALC_RESULT_VERSION });
}

export function parseCalcResult(json: unknown): CalcResult | null {
  if (typeof json !== "string" || json === "") return null;
  let doc: any;
  try {
    doc = JSON.parse(json);
  } catch {
    return null;
  }
  if (!doc || typeof doc !== "object" || Array.isArray(doc)) return null;
  if (doc.v !== CALC_RESULT_VERSION) return null;
  if (!doc.values || typeof doc.values !== "object" || Array.isArray(doc.values)) return null;
  return { ...doc, inputs: doc.inputs ?? {} } as CalcResult;
}

export function resultValue(result: CalcResult | null, paramTypeId: number): string | undefined {
  return result?.values[String(paramTypeId)];
}

/**
 * The figures of one calculation step, held in memory in the shape a parameter
 * read returns. A step collects them here and stores them in its result; they
 * are not written as parameter rows.
 */
export class CalcFigures {
  readonly rows: parameterStruct[] = [];

  set(parameter: number, value: string): void {
    const row = { parameter, value } as parameterStruct;
    const at = this.rows.findIndex((r) => r.parameter === parameter);
    if (at < 0) this.rows.push(row);
    else this.rows[at] = row;
  }

  get(parameter: number): string | undefined {
    return this.rows.find((r) => r.parameter === parameter)?.value;
  }
}

export function valuesFromParams(rows: Pick<parameterStruct, "parameter" | "value">[]): Record<string, string> {
  const values: Record<string, string> = {};
  for (const row of rows) {
    if (row.value === null || row.value === undefined) continue;
    values[String(row.parameter)] = row.value;
  }
  return values;
}

/**
 * The parameter rows of the version that is shown. A version with a document
 * gives the rows `pick` takes from it. A version without one (written by an
 * older build) gives `fallback()`, and so does a document `pick` answers
 * `null` for. An unreadable document gives no rows.
 */
export async function shownParams(
  resultJson: unknown,
  pick: (result: CalcResult) => Record<string, string | number | undefined> | null,
  fallback: () => Promise<parameterStruct[]>
): Promise<parameterStruct[]> {
  if (resultJson === null || resultJson === undefined || resultJson === "") return fallback();
  const result = parseCalcResult(resultJson);
  if (!result) return [];
  const values = pick(result);
  if (!values) return fallback();
  return Object.entries(values)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => ({ parameter: Number(key), value: String(value) }) as parameterStruct);
}
